/**
 * Make Payments Slide-over Alpine.js Component
 *
 * Displays all entities currently in credit and allows batch payment
 * processing via the Accounting::MakePayment service.
 * Tabs: All, Landlords, Contractors, Tenants (Tenancies), Agency.
 */

import consumer from "../channels/consumer"

export function makePaymentsSlideover() {
  return {
    // Slideover state
    open: false,
    zIndex: 100010,
    loading: false,
    error: null,
    submitting: false,
    successMessage: null,

    // Tab
    activeTab: 'all',

    // Data
    payees: [],
    selected: [],
    paymentAmounts: {},   // payeeKey → custom amount in pence (omitted = full balance)
    editingAmount: null,  // payeeKey currently being edited
    editAmountValue: '',  // temporary input value in pounds (string)

    // BACS / payment date
    paymentDate: '',      // ISO date string (yyyy-mm-dd)
    nextWorkingDay: '',   // next working day from server
    bacsFormat: null,     // agency's BACS format key (null = no BACS)

    // ---- Lifecycle ----

    openSlideover() {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.reset()
      this.open = true
      this.toggleAiFab(false)

      if (store?.register) {
        store.register('make-payments', 'list', this)
      }

      this.subscribeToUpdates()
      this.listenForEntityUpdates()
      this.fetchData()
    },

    close() {
      this.cableSubscription?.unsubscribe()
      this.cableSubscription = null
      this._paymentDatePicker?.destroy()
      this._paymentDatePicker = null
      this.stopListeningForEntityUpdates()

      const store = Alpine.store?.('slideovers')
      if (store) {
        store.unregister('make-payments', 'list')
      }
      this.toggleAiFab(true)
      this.$nextTick(() => {
        this.open = false
      })
    },

    // Re-fetch payee data when an entity slideover closes (bank details may have changed)
    listenForEntityUpdates() {
      this._onEntitySlideoverClosed = () => {
        if (this.open) this.fetchData()
      }
      window.addEventListener('account-slideover-closed', this._onEntitySlideoverClosed)
    },

    stopListeningForEntityUpdates() {
      if (this._onEntitySlideoverClosed) {
        window.removeEventListener('account-slideover-closed', this._onEntitySlideoverClosed)
        this._onEntitySlideoverClosed = null
      }
    },

    subscribeToUpdates() {
      const self = this
      this.cableSubscription = consumer.subscriptions.create(
        { channel: "DataUpdatesChannel" },
        {
          received(message) {
            if (message.type === 'payment' && message.action === 'payments_created') {
              // Another session processed payments — refresh the payee list
              if (!self.submitting) {
                self.fetchData()
                self.selected = []
                self.paymentAmounts = {}
              }
            }
          }
        }
      )
    },

    reset() {
      this.payees = []
      this.selected = []
      this.paymentAmounts = {}
      this.editingAmount = null
      this.editAmountValue = ''
      this.paymentDate = ''
      this.nextWorkingDay = ''
      this.bacsFormat = null
      this.activeTab = 'all'
      this.error = null
      this.successMessage = null
      this.submitting = false
    },

    // ---- Data loading ----

    async fetchData() {
      this.loading = true
      this.error = null
      try {
        const response = await fetch('/accounting/accounts/payments/new.json', {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (!response.ok) throw new Error('Failed to load payment data')
        const data = await response.json()
        this.payees = data.payees
        this.nextWorkingDay = data.next_working_day || ''
        this.bacsFormat = data.bacs_format || null
        if (this.bacsFormat && this.nextWorkingDay) {
          this.paymentDate = this.nextWorkingDay
        }
      } catch (e) {
        console.error('Failed to load payees:', e)
        this.error = e.message || 'Failed to load payment data'
      } finally {
        this.loading = false
      }
    },

    // ---- Tabs ----

    get tabs() {
      return [
        { key: 'all', label: 'All', count: this.payees.length },
        { key: 'landlord', label: 'Landlords', count: this.payees.filter(p => p.type_key === 'landlord').length },
        { key: 'contractor', label: 'Contractors', count: this.payees.filter(p => p.type_key === 'contractor').length },
        { key: 'deposit_scheme', label: 'Deposit Schemes', count: this.payees.filter(p => p.type_key === 'deposit_scheme').length },
        { key: 'tenancy', label: 'Tenants', count: this.payees.filter(p => p.type_key === 'tenancy').length },
        { key: 'agency', label: 'Agency', count: this.payees.filter(p => p.type_key === 'agency').length },
      ]
    },

    get filteredPayees() {
      if (this.activeTab === 'all') return this.payees
      return this.payees.filter(p => p.type_key === this.activeTab)
    },

    get selectablePayees() {
      return this.filteredPayees.filter(p => p.has_bank_account)
    },

    get warningPayees() {
      return this.filteredPayees.filter(p => !p.has_bank_account)
    },

    // ---- Selection ----

    payeeKey(payee) {
      if (payee.payable_type === 'Agency') {
        return `${payee.payable_type}-${payee.payable_id}-${payee.account_type}`
      }
      return `${payee.payable_type}-${payee.payable_id}`
    },

    isSelected(payee) {
      return this.selected.includes(this.payeeKey(payee))
    },

    togglePayee(payee) {
      if (!payee.has_bank_account) return
      const key = this.payeeKey(payee)
      const idx = this.selected.indexOf(key)
      if (idx >= 0) {
        this.selected.splice(idx, 1)
      } else {
        this.selected.push(key)
      }
    },

    get allVisibleSelected() {
      if (this.selectablePayees.length === 0) return false
      return this.selectablePayees.every(p => this.selected.includes(this.payeeKey(p)))
    },

    get someVisibleSelected() {
      return this.selectablePayees.some(p => this.selected.includes(this.payeeKey(p))) && !this.allVisibleSelected
    },

    toggleAll() {
      if (this.allVisibleSelected) {
        const visibleKeys = this.selectablePayees.map(p => this.payeeKey(p))
        this.selected = this.selected.filter(k => !visibleKeys.includes(k))
      } else {
        const currentKeys = new Set(this.selected)
        this.selectablePayees.forEach(p => currentKeys.add(this.payeeKey(p)))
        this.selected = [...currentKeys]
      }
    },

    get selectedCount() {
      return this.selected.length
    },

    get selectedTotalPence() {
      let total = 0
      this.payees.forEach(p => {
        if (this.selected.includes(this.payeeKey(p))) {
          total += this.getPaymentPence(p)
        }
      })
      return total
    },

    // ---- Amount editing ----

    getPaymentPence(payee) {
      const key = this.payeeKey(payee)
      return this.paymentAmounts[key] ?? payee.balance_pence
    },

    startEditAmount(payee) {
      if (!payee.has_bank_account) return
      const key = this.payeeKey(payee)
      const currentPence = this.paymentAmounts[key] ?? payee.balance_pence
      this.editAmountValue = (currentPence / 100).toFixed(2)
      this.editingAmount = key
    },

    saveAmount(payee) {
      const key = this.payeeKey(payee)
      const pounds = parseFloat(this.editAmountValue)

      if (isNaN(pounds) || pounds <= 0) {
        this.cancelEditAmount()
        return
      }

      const pence = Math.round(pounds * 100)
      if (pence >= payee.balance_pence) {
        // Full balance or above — remove custom override
        delete this.paymentAmounts[key]
      } else {
        this.paymentAmounts[key] = pence
      }

      this.editingAmount = null
      this.editAmountValue = ''
    },

    cancelEditAmount() {
      this.editingAmount = null
      this.editAmountValue = ''
    },

    hasCustomAmount(payee) {
      return this.payeeKey(payee) in this.paymentAmounts
    },

    resetAmount(payee) {
      delete this.paymentAmounts[this.payeeKey(payee)]
    },

    // ---- Display helpers ----

    get isAgencyTab() {
      return this.activeTab === 'agency'
    },

    get subtitle() {
      return this.isAgencyTab ? 'Agency account drawdowns' : 'People currently in credit'
    },

    get columnHeader() {
      return this.isAgencyTab ? 'ACCOUNT' : 'PAYEE'
    },

    get footerHint() {
      if (this.selectedCount > 0) {
        return `${this.selectedCount} payment${this.selectedCount > 1 ? 's' : ''} selected`
      }
      return ''
    },

    get footerTotal() {
      if (this.selectedCount === 0) return null
      return this.formatPence(this.selectedTotalPence)
    },

    get submitLabel() {
      if (this.selectedCount > 0) {
        if (this.isAgencyTab) return `Draw down ${this.selectedCount}`
        return `Pay ${this.selectedCount} ${this.selectedCount > 1 ? 'people' : 'person'}`
      }
      return this.isAgencyTab ? 'Draw down' : 'Make payments'
    },

    get canSubmit() {
      return this.selectedCount > 0 && !this.submitting
    },

    // ---- Submit ----

    async submit() {
      if (!this.canSubmit) return

      this.submitting = true
      this.error = null

      const selectedPayees = this.payees
        .filter(p => this.selected.includes(this.payeeKey(p)))
        .map(p => {
          const amountPence = this.getPaymentPence(p)
          const data = {
            payable_type: p.payable_type,
            payable_id: p.payable_id,
            name: p.name,
            amount_pence: amountPence
          }
          if (p.payable_type === 'Agency') {
            data.account_type = p.account_type
            data.income_amount_pence = p.account_type === 'income' ? amountPence : 0
            data.vat_amount_pence = p.account_type === 'vat_liability' ? amountPence : 0
          }
          return data
        })

      try {
        const response = await fetch('/accounting/accounts/payments.json', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'X-CSRF-Token': this.csrfToken
          },
          body: JSON.stringify({
            payees: selectedPayees,
            payment_date: this.paymentDate || null
          })
        })

        const data = await response.json()

        if (!response.ok) {
          throw new Error(data.error || 'Failed to process payments')
        }

        if (data.errors && data.errors.length > 0) {
          const errorNames = data.errors.map(e => e.name).join(', ')
          this.error = `Some payments failed: ${errorNames}`
        }

        if (data.payments_created > 0) {
          // Download BACS file if generated
          if (data.bacs_file) {
            this.downloadBacsFile(data.bacs_file)
          }

          this.successMessage = `${data.payments_created} payment${data.payments_created > 1 ? 's' : ''} processed successfully`
          window.dispatchEvent(new CustomEvent('payments-created', { detail: data }))
          setTimeout(() => this.close(), 1500)
        }
      } catch (e) {
        console.error('Payment submission failed:', e)
        this.error = e.message || 'Failed to process payments. Please try again.'
      } finally {
        this.submitting = false
      }
    },

    // ---- UI helpers ----

    toggleAiFab(visible) {
      const fab = document.querySelector('.ai-fab-container')
      if (!fab) return
      fab.style.transition = 'opacity 0.2s ease'
      if (visible) {
        fab.style.display = ''
        requestAnimationFrame(() => { fab.style.opacity = '1' })
      } else {
        fab.style.opacity = '0'
        setTimeout(() => { fab.style.display = 'none' }, 200)
      }
    },

    // ---- BACS helpers ----

    get hasBacsFormat() {
      return !!this.bacsFormat
    },

    initPaymentDatePicker() {
      const input = this.$refs.paymentDateInput
      if (!input || !window.flatpickr) return

      this._paymentDatePicker = window.flatpickr(input, {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'j M Y',
        defaultDate: this.paymentDate,
        minDate: 'today',
        onChange: (selectedDates, dateStr) => {
          this.paymentDate = dateStr
        }
      })
    },

    downloadBacsFile(bacsFile) {
      const blob = new Blob([bacsFile.content], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = bacsFile.filename
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    // ---- Formatters ----

    get csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.content
    },

    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return (pence < 0 ? '-' : '') + '\u00A3' + formatted
    },

    typeBadgeClass(typeKey) {
      switch (typeKey) {
        case 'landlord': return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
        case 'contractor': return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
        case 'tenancy': return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        case 'deposit_scheme': return 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400'
        case 'agency': return 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
        default: return 'bg-gray-100 text-gray-600'
      }
    }
  }
}
